import React, { useState, useRef, useEffect } from 'react';
import { Camera, Image as ImageIcon, X, Upload, AlertCircle, Loader2 } from 'lucide-react';
import { ProductImage } from '../../types';
import { v4 as uuidv4 } from 'uuid';
import { validateImageFile } from '../../utils/imageUtils';
import { supabaseUrl, supabaseAnonKey } from '../../utils/supabase';

interface ImageUploadProps {
  onImagesAdded: (newImages: ProductImage[]) => void;
  productId?: string;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function ImageUpload({ onImagesAdded, productId }: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadImageViaServer = async (file: File): Promise<ProductImage> => {
    console.log('📱 Starting server-side upload for:', {
      name: file.name,
      size: file.size,
      type: file.type,
      productId,
      timestamp: new Date().toISOString()
    });

    const fileBase64 = await fileToBase64(file);

    const response = await fetch(
      `${supabaseUrl}/functions/v1/upload-product-image`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          productId: productId || 'temp-' + Date.now(),
          fileName: file.name,
          fileBase64
        })
      }
    );

    console.log('📡 Server response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ Server upload failed:', errorData);
      throw new Error(errorData.error || `Upload failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Server upload successful:', result);

    if (!result.url) {
      throw new Error('No URL returned from server upload');
    }

    return {
      id: result.image?.id || uuidv4(),
      url: result.url,
      order: result.image?.order || 0
    };
  };

  const uploadImageDirect = async (file: File): Promise<ProductImage> => {
    const formData = new FormData();
    formData.append('file', file);

    console.log('📤 Starting direct upload to Supabase:', {
      name: file.name,
      size: file.size,
      type: file.type,
      timestamp: new Date().toISOString()
    });

    const response = await fetch(
      `${supabaseUrl}/functions/v1/upload-image`,
      {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`
        }
      }
    );

    console.log('📡 Direct upload response status:', response.status);

    if (!response.ok) {
      const responseText = await response.text();
      let errorMessage = responseText;

      try {
        const parsed = JSON.parse(responseText);
        errorMessage = parsed.error || parsed.message || responseText;
      } catch {
        // Keep the raw response text when the function does not return JSON.
      }

      console.error('❌ Direct upload failed:', {
        status: response.status,
        error: errorMessage
      });

      throw new Error(errorMessage || `Upload failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Direct upload successful:', result);

    if (!result.url) {
      throw new Error('No URL returned from direct upload');
    }

    return {
      id: uuidv4(),
      url: result.url,
      order: 0
    };
  };

  const handleFiles = async (files: File[]) => {
    try {
      setError(null);
      setIsLoading(true);
      setUploadProgress(0);

      console.log('📁 Processing files:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));

      if (!files.length) {
        setError('Please select at least one image');
        return;
      }

      const validFiles = files.filter(file => {
        const isValid = validateImageFile(file);
        if (!isValid) {
          console.warn(`❌ Invalid file: ${file.name}`);
        }
        return isValid;
      });

      if (validFiles.length === 0) {
        setError('Please select valid images (JPEG, PNG, WebP, GIF, AVIF, or BMP) under 20MB.');
        return;
      }

      console.log(`✅ Valid files: ${validFiles.length}/${files.length}`);

      const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);

      const uploadedImages: ProductImage[] = [];
      const uploadErrors: string[] = [];
      const totalFiles = validFiles.length;

      for (let i = 0; i < totalFiles; i++) {
        const file = validFiles[i];

        try {
          console.log(`📤 Uploading file ${i + 1}/${totalFiles}: ${file.name}`);

          const uploadedImage = productId
            ? await uploadImageViaServer(file)
            : await uploadImageDirect(file);

          uploadedImages.push({
            ...uploadedImage,
            order: uploadedImages.length
          });

          console.log(`✅ Successfully uploaded ${file.name}:`, uploadedImage);
          setUploadProgress(((i + 1) / totalFiles) * 100);
        } catch (uploadError) {
          const message = getErrorMessage(uploadError);
          console.error(`❌ Failed to upload ${file.name}:`, uploadError);
          uploadErrors.push(`${file.name}: ${message}`);
        }
      }

      if (uploadedImages.length > 0) {
        console.log('🎉 Uploads complete, calling onImagesAdded with:', uploadedImages);
        onImagesAdded(uploadedImages);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        if (cameraInputRef.current) {
          cameraInputRef.current.value = '';
        }

        setTimeout(() => {
          newPreviewUrls.forEach(url => URL.revokeObjectURL(url));
          setPreviewUrls(prev => prev.filter(url => !newPreviewUrls.includes(url)));
        }, 2000);
      }

      if (uploadErrors.length > 0) {
        const prefix = uploadedImages.length > 0
          ? `Uploaded ${uploadedImages.length} of ${validFiles.length} images. `
          : 'Image upload failed. ';
        setError(`${prefix}${uploadErrors.join(' | ')}`);
      } else if (uploadedImages.length > 0) {
        setError(null);
      }
    } catch (handleError) {
      console.error('💥 Error in handleFiles:', handleError);
      setError(`Upload failed: ${getErrorMessage(handleError)}`);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    console.log('📱 File input changed:', files.map(f => f.name));
    handleFiles(files);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer?.files || []);
    console.log('🎯 Files dropped:', files.map(f => f.name));
    handleFiles(files);
  };

  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 cursor-pointer transition-colors">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/avif,image/bmp"
            capture="environment"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
          <Camera className="w-6 h-6 text-gray-400 mr-2" />
          <span className="text-sm font-medium">Take Photo</span>
        </label>

        <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 cursor-pointer transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/avif,image/bmp"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
          <ImageIcon className="w-6 h-6 text-gray-400 mr-2" />
          <span className="text-sm font-medium">Choose from Gallery</span>
        </label>
      </div>

      <div
        ref={dropZoneRef}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center
          transition-colors duration-200 ease-in-out cursor-pointer
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
        `}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-4 text-sm text-gray-600">
          {isDragging ? 'Drop images here...' : 'Drag and drop images here or use buttons above'}
        </p>
        <p className="mt-1 text-xs text-gray-400">
          JPEG, PNG, WebP, GIF, AVIF, or BMP up to 20MB
        </p>
        {productId && (
          <p className="mt-2 text-xs text-blue-600 font-medium">
            🔒 Using secure server upload (bypasses RLS)
          </p>
        )}
      </div>

      {previewUrls.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {previewUrls.map((url, index) => (
            <div key={index} className="relative aspect-square">
              <img
                src={url}
                alt={`Preview ${index + 1}`}
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => {
                  URL.revokeObjectURL(url);
                  setPreviewUrls(prev => prev.filter((_, i) => i !== index));
                }}
                className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="bg-white bg-opacity-75 rounded-lg p-4">
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <div className="flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <p className="text-sm text-center text-gray-600">
              {productId ? 'Uploading via secure server...' : 'Uploading images...'} {Math.round(uploadProgress)}%
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start text-sm text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}
    </div>
  );
}
